import { UNLIMITED } from './usageStatus';

/**
 * Pure, testable builder for the owner-override request. Guarantees TYPE SAFETY:
 *  - a numeric limit ALWAYS sends type 'limit_override' + limit_key + value (never grant/deny), and
 *  - a Boolean/Configurable capability ALWAYS sends type grant|deny + feature_key + NO numeric value, and
 *  - a PRIVATE-BETA action ALWAYS targets the beta-grant/beta-revoke routes with feature_key + reason and
 *    NEVER an overrides path, so beta access can never be written as a commercial entitlement.
 * Throws on a missing reason / commercial basis so a type-unsafe or unaudited call can never be sent.
 * Returns { path, body } relative to the owner API base. No network/axios dependency (unit-testable).
 */
export function buildGovernancePayload({ mode, valueType, tenantRef, decision, featureKey, reason, basis, expiry, allowanceMode, allowance, days, incumbentOverrideId, betaAction }) {
  const t = encodeURIComponent(tenantRef);
  const trimmedReason = (reason || '').trim();
  if (!trimmedReason) throw new Error('REASON_REQUIRED');

  if (mode === 'trial') {
    const d = Number(days);
    if (!Number.isInteger(d) || d <= 0) throw new Error('DAYS_INVALID');
    return { path: `/api/v1/super-admin/commercial/tenants/${t}/trial/extend`, body: { days: d, reason: trimmedReason } };
  }

  // PRIVATE BETA — gate 3 of the activation chain, and a DIFFERENT authority from the grant/deny
  // override below. It carries no commercial basis and no expiry because it is not a commercial
  // decision: it decides whether this tenant may reach an UNRELEASED capability at all, while
  // entitlement decides whether a released one is paid for. Both routes already exist on the owner
  // router under require_superadmin_session; this only names them, so there is no second grant logic.
  if (mode === 'beta') {
    if (betaAction !== 'grant' && betaAction !== 'revoke') throw new Error('BETA_ACTION_REQUIRED');
    if (!featureKey) throw new Error('FEATURE_KEY_REQUIRED');
    const leaf = betaAction === 'grant' ? 'beta-grant' : 'beta-revoke';
    return {
      path: `/api/v1/super-admin/commercial/tenants/${t}/${leaf}`,
      body: { feature_key: featureKey, reason: trimmedReason },
    };
  }

  const VALID_BASIS = ['complimentary', 'promotional', 'contract', 'checkout_required'];
  if (!VALID_BASIS.includes(basis)) throw new Error('COMMERCIAL_BASIS_REQUIRED');

  if (valueType === 'numeric_limit') {
    let value;
    if (allowanceMode === 'unlimited') value = UNLIMITED;
    else {
      const n = Number(allowance);
      if (!Number.isInteger(n) || n < 0) throw new Error('ALLOWANCE_INVALID');
      value = n;
    }
    // GOVERNED REPLACEMENT. When this limit_key ALREADY has an active override, changing it must go through
    // the atomic replace primitive, never a second create: two active overrides for one key is ambiguous
    // authority, and the revoke-then-create alternative opens a fail-open window (a plan with no configured
    // base falls back to "uncapped" between the two writes). The replace route inserts the replacement before
    // superseding the incumbent, so enforcement is continuous, and it CAS-checks the incumbent id so a stale
    // writer is rejected with 409 instead of silently clobbering.
    // No incumbent (first-ever override for this key) keeps the original create path unchanged.
    if (incumbentOverrideId) {
      return {
        path: `/api/v1/super-admin/commercial/tenants/${t}/overrides/${encodeURIComponent(incumbentOverrideId)}/replace`,
        body: { value, reason: trimmedReason, commercial_basis: basis, expiry: expiry || null },
        replaces: incumbentOverrideId,
      };
    }
    return {
      path: `/api/v1/super-admin/commercial/tenants/${t}/overrides`,
      body: { type: 'limit_override', limit_key: featureKey, value, reason: trimmedReason, commercial_basis: basis, expiry: expiry || null },
    };
  }

  // boolean / configurable
  if (decision !== 'grant' && decision !== 'deny') throw new Error('DECISION_REQUIRED');
  return {
    path: `/api/v1/super-admin/commercial/tenants/${t}/overrides`,
    body: { type: decision, feature_key: featureKey, reason: trimmedReason, commercial_basis: basis, expiry: expiry || null },
  };
}
